/**
 * Handing the link over.
 *
 * This used to be a block inside the share screen, which meant it existed for exactly as long as
 * nobody had joined yet — so a room of eight could invite person two and then nobody else, and a
 * send that failed in WhatsApp had no second chance. It is a component now, and it appears in
 * two places: the share screen, and a sheet the session bar can open at any point in a session.
 *
 * The order is deliberate and it is not the order this had before. The share sheet is first and
 * largest, because the overwhelmingly common case is that the person you are meeting is not in
 * the room with you. The QR code is real and worth keeping — a group at a festival gate setting
 * up before they scatter, or a code held to a car window, is faster than any messaging app — but
 * it only works when both phones are already together, so it sits behind a disclosure instead of
 * occupying the top of the most time-critical screen in the product.
 */

import { el } from '../lib/dom';
import { canNativeShare, copyText, shareLink } from '../lib/share';
import { toast } from './feedback';
import { renderQrCanvas } from './qr';

export interface InvitePanelOptions {
  url: string;
  /** What the share sheet says about the link when it lands in a messaging app. */
  message?: string;
}

export interface InvitePanel {
  element: HTMLElement;
  destroy: () => void;
}

export function invitePanel(options: InvitePanelOptions): InvitePanel {
  const message = options.message ?? 'Follow this to find me — no app needed.';

  const actions = el('div', { class: 'invite__actions' });

  const primary = el(
    'button',
    { class: 'button button--primary button--block', type: 'button', 'data-action': 'invite-send' },
    canNativeShare() ? 'Send the link' : 'Copy the link',
  );
  primary.addEventListener('click', () => {
    void shareLink(options.url, message).then((outcome) => {
      if (outcome === 'copied') toast(actions, 'Link copied');
      else if (outcome === 'failed') toast(actions, 'Could not share — the link is below');
    });
  });
  actions.append(primary);

  // Without a share sheet the primary button already copies, so a second one would be noise.
  if (canNativeShare()) {
    const copy = el(
      'button',
      { class: 'button button--ghost button--block', type: 'button' },
      'Copy link',
    );
    copy.addEventListener('click', () => {
      void copyText(options.url).then((copied) => {
        toast(actions, copied ? 'Link copied' : 'Could not copy — select the link below');
      });
    });
    actions.append(copy);
  }

  /* --- the QR code, on request ------------------------------------------ */

  const qrHolder = el('div', { class: 'invite__qr', hidden: true });
  let qrDrawn = false;

  const qrToggle = el(
    'button',
    {
      class: 'invite__disclosure',
      type: 'button',
      'aria-expanded': 'false',
      'data-action': 'invite-qr',
    },
    'Show a QR code',
  );

  qrToggle.addEventListener('click', () => {
    const showing = qrHolder.hidden;
    // Drawn on first open rather than on mount: most invitations are sent, not scanned, and
    // rendering a code nobody looks at costs a frame on the screen where frames matter.
    if (showing && !qrDrawn) {
      qrDrawn = true;
      qrHolder.replaceChildren(
        renderQrCanvas(options.url, { size: 184 }),
        el('p', {
          class: 'invite__qr-hint',
          text: 'For someone standing next to you — point their camera at it.',
        }),
      );
    }
    qrHolder.hidden = !showing;
    qrToggle.setAttribute('aria-expanded', String(showing));
    qrToggle.textContent = showing ? 'Hide the QR code' : 'Show a QR code';
  });

  const element = el(
    'div',
    { class: 'invite' },
    actions,
    el('p', { class: 'share__url mono', text: options.url }),
    qrToggle,
    qrHolder,
  );

  return { element, destroy: () => element.remove() };
}
